import { useMemo, useRef, useState } from 'react'
import * as React from 'react'
// import { colors } from '@configs/styles/config'
import { ShortCourse } from '@ugu/types'
import dynamic from 'next/dynamic'
import { useCartAndWishList } from '@contexts/CartAndWishList'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslation } from 'react-i18next'
// import CloseIcon from '@public/static/icons/close-icon'
import { VerticalCourseUserIcon } from '@public/static/icons/vertical-course-user-icon'
import StarIcon from '@public/static/icons/empty-star'
import { colors } from '@configs/styles/config'
import styled from '@emotion/styled'
import { calculateCourseTime } from '@utils/calculateCourseTime'
// import Icon from '@public/static/Icon'
const HomeFooter = dynamic(() => import('./components/HomeFooter'), {
  loading: () => <div className="h-[45px] w-full"></div>,
})
const SimplePriceView = dynamic(() => import('./components/SimplePriceView'), {
  loading: () => <div className="h-[45px] w-full"></div>,
})
const DiscountApplied = dynamic(() => import('./components/DiscountApplied'), {
  loading: () => <div className="h-[45px] w-full"></div>,
})

type VerticalCourseCardProps = {
  marginLeft?: string
  marginRight?: string
  marginBottom?: string
  marginTop?: string
  course: ShortCourse
  onClick?: Function
  variant?: 'default' | 'cart' | 'wishlist'
  onClose?: () => void | null
  containerRef?: (node: HTMLDivElement) => void
  index?: number
  resultsLength?: number
  setShowCheckoutModal?: React.Dispatch<React.SetStateAction<boolean>>
}

const fallBackImage = '/images/course-placeholder.png'

const VerticalCourseCard: React.FC<VerticalCourseCardProps> = (props) => {
  const { t } = useTranslation(['home', 'common'])
  const {
    course,
    marginLeft = '',
    marginBottom = '20px',
    marginTop = '',
    marginRight = '',
    variant = 'default',
    onClose,
    containerRef,
    index,
    resultsLength,
    setShowCheckoutModal,
  } = props
  const courseRef = useRef<HTMLDivElement>(null)
  const [isCardHovered, setIsCardHovered] = useState<boolean>(false)
  const [fallBackURL, setFallBackURL] = useState('')

  const { discounts } = useCartAndWishList()

  const discount =
    discounts?.find((d) => d?.courses?.includes(course?._id)) || null

  const getCourseLevel = (level?: string) => {
    switch (level) {
      case 'all':
        return t('header.allLevels', { ns: 'common' })
      case 'beginner':
        return t('header.beginner', { ns: 'common' })
      case 'intermediate':
        return t('header.intermediate', { ns: 'common' })
      case 'expert':
        return t('header.expert', { ns: 'common' })
      default:
        return ''
    }
  }

  const courseTime = useMemo(
    () => calculateCourseTime(course?.totalTime),
    [course?.totalTime]
  )

  return (
    <CourseContainer
      onMouseOver={() => {
        setIsCardHovered(true)
      }}
      onMouseOut={() => {
        setIsCardHovered(false)
      }}
      marginLeft={marginLeft}
      marginRight={marginRight}
      marginBottom={marginBottom}
      marginTop={marginTop}
      ref={courseRef}
    >
      {index !== undefined &&
        resultsLength !== undefined &&
        index === resultsLength - 5 && <div ref={containerRef} />}

      <Link
        href={{
          pathname:
            '/courses/' +
            (course?.courseNickname ? course.courseNickname : course?._id),
        }}
        prefetch={false}
        target="_blank"
        className="relative w-full pt-[58%]"
      >
        <Image
          src={course?.presentationalImage || ''}
          alt={course?.title || 'Course Image'}
          width={300}
          height={300}
          placeholder="blur"
          loading="lazy"
          style={{
            transform: `scale(${isCardHovered ? 1.031 : 1}) translateZ(0)`,
            backfaceVisibility: 'hidden',
            backgroundColor: 'rgba(107, 181, 201, 0.2)',
          }}
          className="w-full text-center h-full bg-[rgba(107, 181, 201, 0.2)] will-change-transform transition-transform duration-300 ease-in-out rounded-[15px] object-cover absolute top-0"
          blurDataURL="/images/course-placeholder.png"
          onError={() => {
            if (course) setFallBackURL(fallBackImage)
          }}
          {...(fallBackURL && { unoptimized: true })}
        />
      </Link>
      {(variant === 'cart' || variant === 'wishlist') && onClose && (
        <CloseButton onClick={onClose}>
          {/* <Icon name="close-icon" width="100%" height="100%" /> */}
        </CloseButton>
      )}
      <CourseInfoContainer style={{ flexGrow: 1 }}>
        <Link
          href={{
            pathname:
              '/courses/' +
              (course?.courseNickname ? course.courseNickname : course?._id),
          }}
          style={{ textDecoration: 'none' }}
          prefetch={false}
          target="_blank"
        >
          <CourseTitle className={'font-semiBold'}>{course?.title}</CourseTitle>
        </Link>
        <div className="font-regular gap-[12px] mt-[10px] flex text-uguPurple flex-wrap">
          <div className="flex items-center gap-[0.24rem]">
            <span className="font-bold text-[32px] leading-[5px]">·</span>
            <span className="text-uguPurple text-[14px] sm:text-[13px] relative">
              {new Intl.NumberFormat('en-US', {
                notation: 'compact',
                compactDisplay: 'short',
              }).format(course?.studentsQty || 0)}{' '}
              {t('courseInfo.students')}
            </span>
          </div>
          <div className="flex items-center gap-[0.24rem]">
            <span className="font-bold text-[32px] leading-[5px]">·</span>
            <span className="text-uguPurple text-[14px] sm:text-[13px] relative">
              {courseTime}{' '}
              {courseTime % 10 == 1
                ? t('courseInfo.hour')
                : t('courseInfo.hours')}
            </span>
          </div>
          <div className="flex items-center gap-[0.24rem]">
            <span className="font-bold text-[32px] leading-[5px]">·</span>
            <span className="text-uguPurple text-[14px] sm:text-[13px] relative">
              {getCourseLevel(course?.level)}
            </span>
          </div>
        </div>
        <AuthorReviewBlockWrapper>
          <CourseAuthor className="font-regular">
            <span className="min-w-[15px]">
              {/* <Icon
                name="vertical-course-user-icon"
                width={'16'}
                height={'15'}
                color={colors.uguPurple}
              /> */}
              <VerticalCourseUserIcon
                width={'15'}
                height={'14'}
                // color={colors.uguPurple}
              />
            </span>

            <div className="break-words line-clamp-1 text-[14px] sm:text-[13px]">
              {course?.author?.first_name} {course?.author?.last_name}
            </div>
          </CourseAuthor>
          <div className="text-[14px] sm:text-[14px] flex items-center gap-[5px]">
            {/* <Icon
              name="empty-star"
              width={'16'}
              height={'16'}
              color={colors.uguPurple}
            /> */}
            <StarIcon
              width={'16'}
              height={'16'}
              fill={true}
              color={colors.uguPurple}
            />
            {(course?.rating || 5).toFixed(1)}
          </div>
        </AuthorReviewBlockWrapper>

        {variant !== 'cart' && (
          <HomeFooter
            course={course}
            setShowCheckoutModal={setShowCheckoutModal}
          />
        )}
        {variant === 'cart' ? (
          discount ? (
            <DiscountApplied course={course} discount={discount} />
          ) : (
            <SimplePriceView course={course} />
          )
        ) : (
          <></>
        )}
      </CourseInfoContainer>
    </CourseContainer>
  )
}
export default React.memo(VerticalCourseCard)

const CourseContainer = styled.div<{
  marginLeft: string
  marginRight: string
  marginTop: string
  marginBottom: string
  minWidth?: string
  cyid?: string
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 14.5rem;
  flex: 1 1 0;
  justify-content: space-between;
  margin-left: ${(props) => (props.marginLeft ? props.marginLeft : '')};
  margin-right: ${(props) => (props.marginRight ? props.marginRight : '')};
  //margin-bottom: ${(props) => (props.marginBottom ? props.marginBottom : '')};
  margin-top: ${(props) => (props.marginTop ? props.marginTop : '')};
  cursor: default;
  transition: transform 0.1s ease-in-out;
`

const CourseInfoContainer = styled.div`
  height: 50%;
  border-radius: 0 0 15px 15px;
  padding: 1rem 8px;
  padding-bottom: 0.5rem;
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  /* justify-content: space-between; */
`
const CourseTitle = styled.span`
  color: ${colors.uguPurple};
  width: 100%;
  align-self: flex-start;
  max-height: 3.6rem;
  line-height: 1.2rem;
  height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: break-word;
  font-size: 16px;
  text-overflow: ellipsis;
  @media screen and (max-width: 770px) {
    line-height: 1.35rem;
    max-height: 3.75rem;
  }
`
const CloseButton = styled.button`
  width: 22px;
  height: 22px;
  padding: 5px;
  border-radius: 50px;
  overflow: hidden;
  background: ${colors.uguLighterGrey};
  display: flex;
  border: none;
  outline: none;
  cursor: pointer;
  position: absolute;
  z-index: 12;
  right: 10px;
  top: 10px;
`

const AuthorReviewBlockWrapper = styled.div`
  display: flex;
  /* margin-top: 0.5rem; */
  height: 2.5rem;
  margin-top: 0.3rem;
  justify-content: space-between;
  margin-bottom: 1rem;
  @media (max-width: 640px) {
    margin-top: 0.1rem;
    margin-bottom: 0rem;
  }
`
const CourseAuthor = styled.span`
  display: flex;
  width: 100%;
  color: ${colors.uguPurple};
  font-size: 13px;
  align-items: center;
  gap: 5px;
  max-width: calc(100% - 41px);
`
